import React from 'react';
import styled from 'styled-components';
import { apiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 6rem; /* desktop spacing for sidebar/topbar */
  @media (max-width: 768px) {
    max-width: none;
    margin: 0;
    padding: 0 var(--mobile-page-padding-x);
    width: 100%;
    padding-top: var(--mobile-page-padding-top);
    /* bottom space handled by AppShell wrapper */
  }
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  padding: 1.25rem 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;

  h3 { 
    margin: 0; 
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  p { margin: 0; color: var(--text-light); }
`;

const ExamItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.4rem;
  border-bottom: 1px dashed rgba(0,0,0,0.06);

  .left { display: flex; align-items: center; gap: 0.6rem; min-width: 0; }
  .icon-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(102, 126, 234, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-blue);
    flex-shrink: 0;
  }
  .details { display: flex; flex-direction: column; min-width: 0; }
  .title { font-size: 0.95rem; font-weight: 800; color: var(--heading, #222); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .meta { font-size: 0.8rem; color: var(--text-light); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .badges { display: flex; align-items: center; gap: 0.35rem; }
  .chip { font-size: 0.65rem; font-weight: 800; padding: 0.15rem 0.4rem; border-radius: 999px; color: #fff; }
`;

const gradient = (from: string, to: string) => ({ background: `linear-gradient(135deg, ${from}, ${to})` });

const StudentExams: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [exams, setExams] = React.useState<any[]>([]);
  const formatDate = React.useCallback((iso?: string) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Europe/Amsterdam' }); }
    catch { return new Date(iso).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' }); }
  }, [i18n.language]);

  const hSize = window.innerWidth <= 768 ? 'var(--font-size-h2)' : 'var(--font-size-h1)';

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const listResp = await apiService.get<any>('/api/exams/student/my');
      const list = Array.isArray((listResp as any)?.data) ? (listResp as any).data : (Array.isArray(listResp) ? listResp : []);
      setExams(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load exams');
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Flattened rendering: single list (no pass/fail grouping)
  const computeStatus = (e: any): 'UPCOMING'|'COMPLETED'|'PASSED'|'FAILED' => {
    if (e?.status) return e.status;
    try {
      const now = new Date();
      const dateStr = e?.startTime || e?.examDate;
      const dt = dateStr ? new Date(dateStr) : null;

      if (dt) {
        if (dt > now) return 'UPCOMING';
        
        // 1-hour grace period for non-results
        const oneHourAfter = new Date(dt.getTime() + 60 * 60 * 1000);
        if (now < oneHourAfter && typeof e?.result === 'boolean' && !e.result) {
          return 'UPCOMING';
        }
      }

      if (typeof e?.result === 'boolean' || e?.result !== null) {
        if (e.result === true || e.result === 'PASSED') return 'PASSED';
        if (e.result === false || e.result === 'FAILED') return 'FAILED';
      }
      return 'COMPLETED';
    } catch {
      return 'COMPLETED';
    }
  };

  const { upcomingExams, pastExams } = React.useMemo(() => {
    const arr = Array.isArray(exams) ? [...exams] : [];
    const upcoming = arr.filter(e => computeStatus(e) === 'UPCOMING')
      .sort((a, b) => {
        const ta = a?.startTime ? Date.parse(a.startTime) : 0;
        const tb = b?.startTime ? Date.parse(b.startTime) : 0;
        return ta - tb; // Soonest first for upcoming
      });
    
    const past = arr.filter(e => computeStatus(e) !== 'UPCOMING')
      .sort((a, b) => {
        const ta = a?.startTime ? Date.parse(a.startTime) : 0;
        const tb = b?.startTime ? Date.parse(b.startTime) : 0;
        return tb - ta; // Latest first for history
      });

    return { upcomingExams: upcoming, pastExams: past };
  }, [exams]);

  const renderExamItem = (e: any) => {
    const status = computeStatus(e);
    const chip = status === 'PASSED' ? {style: gradient('#11998e','#38ef7d'), label: t('portal.exams.status.passed','PASSED')} :
                status === 'FAILED' ? {style: gradient('#fc4a1a','#f7b733'), label: t('portal.exams.status.failed','FAILED')} :
                status === 'UPCOMING' ? {style: gradient('#667eea','#764ba2'), label: t('portal.exams.status.upcoming','UPCOMING')} :
                {style: gradient('#667eea','#764ba2'), label: t('portal.exams.status.completed','COMPLETED')};

    let icon = <AssignmentIcon fontSize="small" />;
    let iconBg = 'rgba(102, 126, 234, 0.1)';
    let iconColor = 'var(--primary-blue)';

    if (status === 'PASSED') {
      icon = <CheckCircleIcon fontSize="small" />;
      iconBg = 'rgba(17, 153, 142, 0.1)';
      iconColor = '#11998e';
    } else if (status === 'FAILED') {
      icon = <CancelIcon fontSize="small" />;
      iconBg = 'rgba(252, 74, 26, 0.1)';
      iconColor = '#fc4a1a';
    } else if (status === 'COMPLETED') {
      icon = <HistoryIcon fontSize="small" />;
      iconBg = 'rgba(107, 114, 128, 0.1)';
      iconColor = '#6b7280';
    }

    return (
      <div key={`exam-${e.id}`} style={{ borderBottom: '1px dashed rgba(0,0,0,0.06)' }}>
        <div role="button" onClick={()=> navigate(`/portal/student/exams/${e.id}`)} style={{ cursor:'pointer' }}>
          <ExamItem>
            <div className="left">
              <div className="icon-wrapper" style={{ background: iconBg, color: iconColor }}>
                {icon}
              </div>
              <div className="details">
                <div className="title">{formatDate(e.startTime)}{e.location ? ` • ${e.location}` : ''}</div>
                <div className="meta">{t('portal.exams.tap_details', 'Tap for details')}</div>
              </div>
            </div>
            <div className="badges">
              <span className="chip" style={chip.style as any}>
                {(() => {
                  const now = new Date();
                  const dt = e.startTime ? new Date(e.startTime) : null;
                  if (dt && dt > now) return '—';
                  return chip.label;
                })()}
              </span>
            </div>
          </ExamItem>
        </div>
      </div>
    );
  };

  return (
    <PageWrapper>
      <ContentWrapper>
        {loading && exams.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>{t('portal.common.loading', 'Loading…')}</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '1.25rem', color: '#b00020', fontWeight: 700 }}>⚠️ {error}</div>
        ) : null}

        {/* Upcoming Exams */}
        {upcomingExams.length > 0 && (
          <SectionCard>
            <Header>
              <div>
                <h3>{t('portal.exams.upcoming', 'Upcoming Exams')}</h3>
                <p>{t('portal.exams.upcoming_sub', 'Your scheduled exams')}</p>
              </div>
            </Header>
            {upcomingExams.map(renderExamItem)}
          </SectionCard>
        )}

        {/* History */}
        <SectionCard>
          <Header>
            <div>
              <h3>{t('portal.exams.history', 'Exam History')}</h3>
              <p>{t('portal.exams.history_sub', 'Past exams and results')}</p>
            </div>
          </Header>

          {loading && exams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>{t('portal.common.loading', 'Loading…')}</div>
          ) : pastExams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>{t('portal.exams.none', 'No past exams yet.')}</div>
          ) : (
            <div>
              {pastExams.map(renderExamItem)}
            </div>
          )}
        </SectionCard>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default StudentExams;
